import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useClientRequest, dashboardPath } from './clientApi';
import { clientDefaults } from './profileDefaults';
import PrimaryButton from '../../../DesignSystem/Buttons/PrimaryButton';
import IconButton from '../../../DesignSystem/Buttons/IconButton';
import { ReactComponent as DownloadIcon } from '../../../../assets/icons/download.svg';
import { downloadClientCsv } from './clientExport';
import { validateProfile } from './clientModel';
import Popup from '../../../DesignSystem/Popups/Popup';
import NewInputField from '../../../DesignSystem/Inputs/NewInputField';
import NewDateField from '../../../DesignSystem/Inputs/NewDateField';
import { ReactComponent as EditIcon } from '../../../../assets/icons/edit.svg';
function canonical(value) {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort().filter(key => value[key] !== '' && value[key] != null).map(key => [key, canonical(value[key])]));
  if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value)) return Number(value);
  return value;
}
const signature = value => JSON.stringify(canonical(value));
const inputs = draft => ({ values: draft.values, funds: draft.funds, tranches: draft.tranches });
const comparable = draft => ({ ...inputs(draft), result: draft.result });
const ClientContext = createContext(null);
export const usePensionClient = () => useContext(ClientContext);

export function ClientProvider({ children }) {
  const { clientId, ownerEmail } = useParams();
  const clientRequest = useClientRequest();
  const [client, setClient] = useState(null);
  const [calculators, setCalculators] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const drafts = useRef({});
  const changeCounter = useRef(0);
  const baseline = useRef({ profile: null, calculators: {} });
  const profileRef = useRef(null);
  const refreshDirty = useCallback(() => {
    setDirty(signature(profileRef.current) !== signature(baseline.current.profile) || Object.entries(drafts.current).some(([id, draft]) => signature(comparable(draft)) !== signature(comparable(baseline.current.calculators[id] || { values: {} }))));
  }, []);
  const registerBaseline = useCallback((id, draft) => {
    if (!baseline.current.calculators[id]) baseline.current.calculators[id] = draft;
  }, []);
  useEffect(() => {
    let active = true;
    setClient(null); setError(''); setDirty(false); drafts.current = {}; changeCounter.current = 0;
    if (!clientRequest) return;
    clientRequest(`pension-clients/${encodeURIComponent(ownerEmail)}/${encodeURIComponent(clientId)}`).then(data => {
      if (!active) return;
      baseline.current = { profile: data.profile, calculators: { ...data.calculators } }; profileRef.current = data.profile;
      drafts.current = data.calculators || {}; setCalculators(drafts.current); setClient(data);
    }).catch(failure => { if (active) setError(failure.message); });
    return () => { active = false; };
  }, [clientId, ownerEmail, clientRequest]);
  useEffect(() => {
    const warn = event => { if (dirty) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const getDraft = useCallback(id => drafts.current[id], []);
  const updateDraft = useCallback((id, draft, allowRestore = false) => {
    const saved = baseline.current.calculators[id];
    const restored = allowRestore && saved && !draft.result && signature(inputs(saved)) === signature(inputs(draft)) ? saved : draft;
    if (JSON.stringify(drafts.current[id]) === JSON.stringify(restored)) return restored;
    draft = restored;
    drafts.current = { ...drafts.current, [id]: draft }; setCalculators(drafts.current);
    changeCounter.current++; refreshDirty(); setMessage('');
    return restored;
  }, [refreshDirty]);
  function updateProfile(input) {
    const profile = validateProfile(input);
    if (JSON.stringify(profile) === JSON.stringify(client.profile)) return;
    const previous = clientDefaults(client.profile);
    const next = clientDefaults(profile);
    const shared = Object.fromEntries([...new Set([...Object.keys(previous), ...Object.keys(next)])].filter(key => key !== 'taxYear').map(key => [key, next[key] ?? '']));
    drafts.current = Object.fromEntries(Object.entries(drafts.current).map(([id, draft]) => [id, { ...draft, values: { ...draft.values, ...shared }, result: null, status: 'in-progress', statusMessage: 'Client details changed. Calculate to update the results.' }]));
    setCalculators(drafts.current);
    setClient(current => ({ ...current, profile }));
    profileRef.current = profile;
    changeCounter.current++; refreshDirty(); setMessage('');
  }
  async function save() {
    if (!client || saving) return;
    setSaving(true); setError(''); setMessage('');
    const counter = changeCounter.current;
    try {
      const data = await clientRequest(`pension-clients/${encodeURIComponent(client.ownerEmail)}/${encodeURIComponent(client.id)}`, { method: 'PUT', action: 'save-progress', body: { profile: client.profile, calculators: drafts.current, revision: client.revision } });
      baseline.current = { profile: data.profile, calculators: { ...data.calculators } };
      refreshDirty();
      setClient(current => counter === changeCounter.current ? data : { ...current, revision: data.revision });
      if (counter === changeCounter.current) { setDirty(false); setMessage('All progress saved.'); }
      else setMessage('Progress saved. You have newer unsaved changes.');
    } catch (failure) { setError(failure.message); }
    finally { setSaving(false); }
  }
  if (!client || client.id !== clientId || client.ownerEmail !== ownerEmail) return <main className="fp-submission-container"><p className="FP_Body" role="status">{error || 'Loading client…'}</p><Link to={dashboardPath}>Back to clients</Link></main>;
  return <ClientContext.Provider value={{ client, calculators, defaults: clientDefaults(client.profile), getDraft, updateDraft, registerBaseline, updateProfile, dirty, saving, save, error, message }}>{children}</ClientContext.Provider>;
}

export function ClientHeader() {
  const context = usePensionClient();
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState({});
  const [editError, setEditError] = useState('');
  const formRef = useRef(null);
  if (!context) return null;
  const { client, dirty, saving, save, error, message } = context;
  const dob = client.profile.dateOfBirth ? new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(client.profile.dateOfBirth)) : 'Not provided';
  const change = event => setProfile(previous => ({ ...previous, [event.target.name]: event.target.value }));
  function apply(event) {
    event.preventDefault();
    try { context.updateProfile(profile); setEditing(false); setEditError(''); }
    catch (failure) { setEditError(failure.message); }
  }
  return <div className="fit-column gap-s" style={{ width: '100%', minWidth: 0 }}>
    <div className="display-flex gap-m" style={{ display: 'flex', flexDirection: 'row', width: '100%', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <div className="fit-column gap-xs" style={{ width: 'auto', flex: '1 1 0', alignItems: 'flex-start', textAlign: 'left', minWidth: 0 }}>
        <span className="FP_Subtitle_Semibold text-primary-1" style={{ overflowWrap: 'anywhere' }}>{client.profile.name}</span>
        <span className="FP_Content">DOB: {dob}</span>
        <span className="FP_Content">Retirement age: {client.profile.retirementAge ?? 'Not provided'}</span>
      </div>
      <div className="display-flex gap-s" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', marginLeft: 'auto', flexShrink: 0 }}>
        <IconButton icon={<EditIcon aria-hidden />} label="Edit client profile" disabled={saving} onClick={() => { setProfile({ ...client.profile, retirementAge: client.profile.retirementAge ?? '' }); setEditError(''); setEditing(true); }} />
        <IconButton icon={<DownloadIcon aria-hidden />} label="Download client data" onClick={() => downloadClientCsv(client, context.calculators)} />
        <PrimaryButton type="button" label={saving ? 'Saving…' : 'Save progress'} disabled={saving || !dirty} onClick={save} className="ds-button-uniform" />
      </div>
    </div>
    <p className="FP_Help_Text" role="status">{saving ? 'Saving your changes…' : message || (dirty ? 'Unsaved changes. Click Save progress to save this client.' : 'No unsaved changes.')}</p>
    {error && <p role="alert" className="FP_Content text-error">{error}</p>}
    <Popup open={editing} title="Edit client profile" titleClassName="FP_Subtitle_Semibold text-primary-1" titleAlign="left" showCancel={false} confirmLabel="Apply changes" className="ds-confirmation-popup--form" onCancel={() => setEditing(false)} onConfirm={() => formRef.current?.requestSubmit()}>
      <form ref={formRef} onSubmit={apply} className="fit-column gap-m">
        <NewInputField id="edit-client-name" name="name" label="Client name" value={profile.name || ''} onChange={change} required inputProps={{ maxLength: 200 }} />
        <div className="grid2 gap-m">
          <NewDateField id="edit-client-dob" name="dateOfBirth" label="Date of birth" required value={profile.dateOfBirth || ''} onChange={change} min="1900-01-01" max={new Date().toISOString().slice(0, 10)} />
          <NewInputField id="edit-client-retirement" name="retirementAge" label="Retirement age (optional)" type="number" min={1} step={1} value={profile.retirementAge ?? ''} onChange={change} inputProps={{ max: 120 }} />
        </div>
        <p className="FP_Help_Text">Click Save progress after applying changes to save the updated profile.</p>
        {editError && <p role="alert" className="FP_Content text-error">{editError}</p>}
      </form>
    </Popup>
  </div>;
}

export function useClientDraft(id, draft, restore) {
  const context = usePensionClient();
  const update = context?.updateDraft;
  const register = context?.registerBaseline;
  useEffect(() => { register?.(id, draft); }, [id, draft, register]); // Capture the initial untouched form once per calculator.
  // First render registers the baseline without making an untouched client dirty.
  const previous = useRef(JSON.stringify(draft));
  const previousInputs = useRef(signature(inputs(draft)));
  useEffect(() => {
    const next = JSON.stringify(draft);
    if (previous.current === next) return;
    previous.current = next;
    const nextInputs = signature(inputs(draft));
    const changedInputs = previousInputs.current !== nextInputs;
    previousInputs.current = nextInputs;
    const effective = update?.(id, draft.status === 'calculating' ? { ...draft, status: 'in-progress', statusMessage: 'Calculate to update the results.' } : draft, changedInputs);
    if (changedInputs && effective && JSON.stringify(effective) !== next) restore?.(effective);
  }, [id, draft, update, restore]);
}
